import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
} from '@mui/material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import customerService from '../services/customerService';
import routerService from '../services/routerService';
import spopService from '../services/spopService';
import odpService from '../services/odpService';
import packageService from '../services/packageService';
import PPPSecretSelectionDialog from './PPPSecretSelectionDialog';
import CreatePPPSecretDialog from './CreatePPPSecretDialog';

interface EditCustomerDialogProps {
  open: boolean;
  onClose: () => void;
  customer: any;
}

interface CustomerFormData {
  customer_code: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  username: string;
  password: string;
  profile: string;
  package_id?: string;
  status: 'active' | 'inactive' | 'suspended' | 'free' | 'NonInternet';
  activation_date?: string;
  router_id: string;
  spop_id?: string;
  odp_id?: string;
}

interface CustomerUpdateData {
  customer_code: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  username: string;
  password?: string;
  profile: string;
  package_id?: string;
  status: 'active' | 'inactive' | 'suspended' | 'free' | 'NonInternet';
  activation_date?: string;
  router_id: string;
  spop_id?: string;
  odp_id?: string;
}

const EditCustomerDialog: React.FC<EditCustomerDialogProps> = ({ open, onClose, customer }) => {
  // Helper function to format bytes
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const [formData, setFormData] = useState<CustomerFormData>({
    customer_code: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    username: '',
    password: '',
    profile: '',
    status: 'active',
    activation_date: '',
    router_id: '',
    spop_id: '',
    odp_id: '',
  });

  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [routerAutoSelected, setRouterAutoSelected] = useState(false);
  const [isCreatingPPP, setIsCreatingPPP] = useState(false);
  const [pppCreationStatus, setPppCreationStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [pppMessage, setPppMessage] = useState('');
  const [showPPPSelectionDialog, setShowPPPSelectionDialog] = useState(false);
  const [showCreatePPPDialog, setShowCreatePPPDialog] = useState(false);
  const [existingPPPSecrets, setExistingPPPSecrets] = useState<any[]>([]);
  const [isLoadingPPPSecrets, setIsLoadingPPPSecrets] = useState(false);
  const [pppSecretsError, setPppSecretsError] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [pppUpdateResult, setPppUpdateResult] = useState<{
    success: boolean;
    message: string;
    action: string;
  } | null>(null);

  const queryClient = useQueryClient();

  // Populate form when customer data changes
  useEffect(() => {
    if (customer && open) {
      setFormData({
        customer_code: customer.customer_code || '',
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        username: customer.username || '',
        password: '', // Don't populate password for security
        profile: customer.profile || '',
        package_id: customer.package_id || '',
        status: customer.status || 'active',
        activation_date: customer.activation_date || '',
        router_id: customer.router_id || '',
        spop_id: customer.spop_id || customer.spop?.id || '',
        odp_id: customer.odp_id || customer.odp?.id || '',
      });
    }
  }, [customer, open]);

  // Get Routers for dropdown
  const { data: routersData, isLoading: routersLoading } = useQuery({
    queryKey: ['routers-all'],
    queryFn: async () => {
      const response = await routerService.getRouters(1, 1000);
      return response.data.routers;
    },
  });

  // Get SPOPs for dropdown
  const { data: spopsData, isLoading: spopsLoading } = useQuery({
    queryKey: ['spops-all'],
    queryFn: async () => {
      const response = await spopService.getAllSPOPs();
      return response.data.data.spops;
    },
  });

  // Get Packages for dropdown (filtered by selected SPOP)
  const { data: packagesData, isLoading: packagesLoading } = useQuery({
    queryKey: ['packages-by-spop', formData.spop_id],
    queryFn: async () => {
      // Jika SPOP dipilih, filter paket berdasarkan SPOP
      if (formData.spop_id) {
        return await packageService.getAllActivePackages(formData.spop_id);
      }
      
      // Jika SPOP belum dipilih, return semua paket aktif
      return await packageService.getAllActivePackages();
    },
    enabled: true, // Always enabled karena kita butuh data paket
  });

  // Get ODPs for dropdown (filtered by selected SPOP)
  const { data: odpsData, isLoading: odpsLoading } = useQuery({
    queryKey: ['odps-by-spop', formData.spop_id],
    queryFn: async () => {
      if (!formData.spop_id) return [];
      const response = await odpService.getODPs(1, 1000, '', undefined, formData.spop_id);
      return response.data.odps;
    },
    enabled: !!formData.spop_id,
  });

  // Function to generate customer code
  const generateCustomerCode = async () => {
    const selectedSpop = spopsData?.find((spop: any) => spop.id === formData.spop_id);
    const selectedOdp = odpsData?.find((odp: any) => odp.id === formData.odp_id);
    
    if (selectedSpop && selectedOdp) {
      setIsGeneratingCode(true);
      try {
        // Use the new service method to generate unique customer code
        const customerCode = await customerService.generateUniqueCustomerCode(
          selectedSpop.areaCode, 
          selectedOdp.odpCode
        );
        
        console.log('Generated unique customer code:', customerCode);
        
        setFormData(prev => ({
          ...prev,
          customer_code: customerCode
        }));
        
        // Clear any previous PPP error messages when new code is generated
        if (pppCreationStatus === 'error' && pppMessage.includes('Customer code')) {
          setPppCreationStatus('idle');
          setPppMessage('');
        }
        
      } catch (error) {
        console.error('Error generating customer code:', error);
        // Enhanced fallback with more uniqueness
        const timestamp = Date.now().toString();
        const randomSuffix = Math.random().toString(36).substr(2, 3).toUpperCase();
        const fallbackNumber = timestamp.slice(-4) + randomSuffix.slice(0, 2);
        const customerCode = `${selectedSpop.areaCode}${selectedOdp.odpCode}${fallbackNumber}`;
        
        console.log('Fallback customer code:', customerCode);
        
        setFormData(prev => ({
          ...prev,
          customer_code: customerCode
        }));
      } finally {
        setIsGeneratingCode(false);
      }
    }
  };

  // Auto-generate customer code when dependencies change (only if customer code is empty)
  React.useEffect(() => {
    if (formData.spop_id && formData.odp_id && !formData.customer_code) {
      generateCustomerCode();
    }
  }, [formData.spop_id, formData.odp_id, spopsData, odpsData]);

  // Clear PPP status when customer code changes and validate uniqueness
  React.useEffect(() => {
    setPppCreationStatus('idle');
    setPppMessage('');
    
    // Validate customer code uniqueness if it exists
    const validateCustomerCode = async () => {
      if (formData.customer_code && customer && formData.customer_code !== customer.customer_code) {
        try {
          const existingCustomers = await customerService.getCustomers(1, 1000, formData.customer_code);
          const customers = existingCustomers.data?.customers || [];
          
          if (customers.length > 0) {
            setPppCreationStatus('error');
            setPppMessage(`⚠️ Customer code "${formData.customer_code}" sudah digunakan. Silakan generate ulang atau ubah manual.`);
          }
        } catch (error) {
          console.error('Error validating customer code:', error);
        }
      }
    };
    
    // Debounce validation to avoid excessive API calls
    const timeoutId = setTimeout(validateCustomerCode, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.customer_code, customer]);

  // Validation for customer code generation
  const canGenerateCode = () => {
    return formData.spop_id && formData.odp_id;
  };

  const getCodeGenerationMessage = () => {
    if (!formData.spop_id) return '⚠️ Pilih SPOP terlebih dahulu';
    if (!formData.odp_id) return '⚠️ Pilih ODP terlebih dahulu';
    if (isGeneratingCode) return '🔄 Sedang generate kode customer...';
    if (formData.customer_code) {
      // Check if there's a conflict message
      if (pppCreationStatus === 'error' && pppMessage.includes('Customer code')) {
        return pppMessage;
      }
      return '✅ Kode customer tersedia';
    }
    return '🔄 Kode customer akan digenerate otomatis';
  };

  // Function to create PPP Secret on MikroTik (Sinkronisasi)
  const createPPPSecret = async () => {
    if (!formData.router_id || !formData.customer_code || !formData.name) {
      setPppCreationStatus('error');
      setPppMessage('Harap lengkapi Router, Kode Customer, dan Nama terlebih dahulu');
      return;
    }

    setIsCreatingPPP(true);
    setPppCreationStatus('idle');
    setPppMessage('');

    try {
      // Check if there are existing PPP secrets for synchronization
      const existingSecretsResponse = await routerService.getPPPSecrets(formData.router_id);
      
      if (existingSecretsResponse.success && existingSecretsResponse.data && existingSecretsResponse.data.length > 0) {
        // Filter out secrets that might conflict with current customer
        const availableSecrets = existingSecretsResponse.data.filter((secret: any) => {
          // Don't show current customer's secret if it exists
          return secret.name !== formData.username;
        });

        if (availableSecrets.length > 0) {
          setExistingPPPSecrets(availableSecrets);
          setShowPPPSelectionDialog(true);
          return;
        }
      }

      // If no existing secrets found
      setPppCreationStatus('error');
      setPppMessage('Tidak ada PPP Secret yang ditemukan di router untuk sinkronisasi. Gunakan opsi "Buat PPP Baru" untuk membuat yang baru.');
    } catch (error: any) {
      setPppCreationStatus('error');
      setPppMessage(error.response?.data?.message || 'Error saat mencari PPP Secret untuk sinkronisasi');
    } finally {
      setIsCreatingPPP(false);
    }
  };

  // Function to create new PPP Secret
  const createNewPPPSecret = async (secretData: {
    username: string;
    password: string;
    profile: string;
  }) => {
    if (!formData.router_id || !formData.customer_code || !formData.name) {
      setPppCreationStatus('error');
      setPppMessage('Harap lengkapi Router, Kode Customer, dan Nama terlebih dahulu');
      return;
    }

    setIsCreatingPPP(true);
    setPppCreationStatus('idle');
    setPppMessage('');

    try {
      // Create new PPP Secret with custom data
      const response = await routerService.createPPPSecret(formData.router_id, {
        customerCode: formData.customer_code,
        name: formData.name,
        username: secretData.username,
        password: secretData.password,
        profile: secretData.profile
      });

      if (response.success && response.data) {
        // Auto-fill form with created PPP data
        setFormData(prev => ({
          ...prev,
          username: secretData.username,
          password: secretData.password,
          profile: secretData.profile
        }));
        
        setPppCreationStatus('success');
        setPppMessage(`✅ PPP Secret berhasil dibuat di MikroTik: ${secretData.username}`);
      } else {
        setPppCreationStatus('error');
        setPppMessage('Gagal membuat PPP Secret di MikroTik');
      }
    } catch (error: any) {
      setPppCreationStatus('error');
      setPppMessage(error.response?.data?.message || 'Error saat membuat PPP Secret baru');
    } finally {
      setIsCreatingPPP(false);
    }
  };

  // Function to handle PPP secret selection from existing ones (Sinkronisasi)
  const handlePPPSecretSelection = async (selectedSecret: any) => {
    setShowPPPSelectionDialog(false);
    setIsLoadingPPPSecrets(true);
    setPppSecretsError('');

    try {
      // Check multiple criteria to ensure no conflicts
      const existingCustomerCheck = await customerService.getCustomers(1, 1000, '');
      const customers = existingCustomerCheck.data?.customers || [];
      
      // Check for username conflict (exclude current customer)
      const usernameExists = customers.some((cust: any) => cust.username === selectedSecret.name && cust.id !== customer?.id);
      if (usernameExists) {
        setPppCreationStatus('error');
        setPppMessage(`⚠️ Username "${selectedSecret.name}" sudah digunakan oleh customer lain. Pilih PPP Secret yang berbeda.`);
        setIsLoadingPPPSecrets(false);
        return;
      }

      // If no conflicts, auto-fill form
      setFormData(prev => ({
        ...prev,
        username: selectedSecret.name,
        profile: selectedSecret.profile || ''
      }));
      
      setPppCreationStatus('success');
      setPppMessage(`✅ Data PPP Secret berhasil disinkronkan: ${selectedSecret.name}`);
      
    } catch (error: any) {
      setPppCreationStatus('error');
      if (error.response?.data?.message?.includes('sudah ada di database')) {
        setPppMessage('⚠️ PPP Secret ini sudah terhubung dengan customer lain. Pilih yang berbeda.');
      } else {
        setPppMessage(error.response?.data?.message || 'Error saat melakukan sinkronisasi PPP Secret');
      }
    } finally {
      setIsLoadingPPPSecrets(false);
    }
  };

  // Function to handle creating new PPP secret when user chooses not to use existing ones
  const handleCreateNewPPPSecret = async () => {
    setShowPPPSelectionDialog(false);
    // Redirect to create new PPP Secret dialog
    setShowCreatePPPDialog(true);
  };

  // Check if PPP creation is possible
  const canCreatePPP = () => {
    return formData.router_id && formData.customer_code && formData.name.trim();
  };

  const updateCustomerMutation = useMutation({
    mutationFn: (data: CustomerUpdateData) => customerService.updatePPPSecret(customer.id, data as any),
    onSuccess: (result) => {
      console.log('Update customer result:', result);
      
      // Set success message and PPP update result
      setSuccessMessage('Customer berhasil diupdate!');
      setPppUpdateResult(result.pppUpdateResult || null);
      setShowSuccessDialog(true);
      
      // Invalidate queries and close dialog after showing success
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (error: any) => {
      console.error('Update customer error:', error);
    }
  });

  const resetForm = () => {
    setFormData({
      customer_code: '',
      name: '',
      email: '',
      phone: '',
      address: '',
      username: '',
      password: '',
      profile: '',
      status: 'active',
      activation_date: '',
      router_id: '',
      spop_id: '',
      odp_id: '',
    });
    setIsGeneratingCode(false);
    setRouterAutoSelected(false);
    setIsCreatingPPP(false);
    setPppCreationStatus('idle');
    setPppMessage('');
    setShowPPPSelectionDialog(false);
    setShowCreatePPPDialog(false);
    setExistingPPPSecrets([]);
    setIsLoadingPPPSecrets(false);
    setPppSecretsError('');
    setShowSuccessDialog(false);
    setSuccessMessage('');
    setPppUpdateResult(null);
  };

  const handleInputChange = (field: keyof CustomerFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSelectChange = (field: keyof CustomerFormData) => (
    e: any
  ) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
      // Reset ODP when SPOP changes
      ...(field === 'spop_id' && { odp_id: '' }),
      // Auto-fill profile when package changes
      ...(field === 'package_id' && value && {
        profile: packagesData?.find((pkg: any) => pkg.id === value)?.profile_name || ''
      }),
    }));

    // Clear PPP status when router changes
    if (field === 'router_id') {
      setPppCreationStatus('idle');
      setPppMessage('');
    }

    // Auto-select router when SPOP is selected
    if (field === 'spop_id' && value && routersData) {
      const associatedRouter = routersData.find((router: any) => router.spopdId === value);
      if (associatedRouter) {
        setFormData(prev => ({ ...prev, router_id: String(associatedRouter.id) }));
        setRouterAutoSelected(true);
      } else {
        setRouterAutoSelected(false);
      }
      
      // Clear PPP status when SPOP changes
      setPppCreationStatus('idle');
      setPppMessage('');
    }
  };

  const buildUpdatePayload = (): CustomerUpdateData => {
    const payload: CustomerUpdateData = {
      customer_code: formData.customer_code.trim(),
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim() || customer.phone,
      address: formData.address.trim() || customer.address,
      username: formData.username.trim(),
      profile: formData.profile.trim() || customer.profile,
      status: formData.status,
      router_id: formData.router_id,
      activation_date: formData.activation_date || undefined,
      spop_id: formData.spop_id || undefined,
      odp_id: formData.odp_id || undefined,
      package_id: formData.package_id || undefined,
    };

    const newPassword = formData.password.trim();
    if (newPassword) {
      payload.password = newPassword;
    }

    return payload;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.username || !formData.router_id) {
      return;
    }

    updateCustomerMutation.mutate(buildUpdatePayload());
  };

  const handleClose = () => {
    if (!updateCustomerMutation.isPending) {
      // Reset states
      setShowSuccessDialog(false);
      setSuccessMessage('');
      setPppUpdateResult(null);
      resetForm();
      onClose();
    }
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    setSuccessMessage('');
    setPppUpdateResult(null);
    resetForm();
    onClose();
  };

  // Function to handle manual PPP secret input (untuk auto-fill form tanpa membuat PPP)
  const handleCreatePPPSecretSubmit = async (secretData: {
    username: string;
    password: string;
    profile: string;
  }) => {
    setShowCreatePPPDialog(false);
    
    // Auto-fill form dengan data PPP baru atau update form
    setFormData(prev => ({
      ...prev,
      username: secretData.username,
      password: secretData.password,
      profile: secretData.profile
    }));
    
    setPppCreationStatus('success');
    setPppMessage('Form berhasil diisi dengan data PPP. Customer akan diupdate dengan data ini.');
  };

  if (!customer) return null;

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle>Edit Customer</DialogTitle>
          
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <Box display="flex" gap={2}>
                <TextField
                  label="Kode Customer"
                  value={formData.customer_code}
                  onChange={handleInputChange('customer_code')}
                  required
                  fullWidth
                  placeholder="CUST001"
                />
                <TextField
                  label="Nama Lengkap"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  required
                  fullWidth
                  placeholder="John Doe"
                />
              </Box>
              
              <Box display="flex" gap={2}>
                <TextField
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  required
                  fullWidth
                  placeholder="john@example.com"
                />
                <TextField
                  label="No. Telepon"
                  value={formData.phone}
                  onChange={handleInputChange('phone')}
                  fullWidth
                  placeholder="081234567890"
                />
              </Box>
                
              <TextField
                label="Alamat"
                value={formData.address}
                onChange={handleInputChange('address')}
                fullWidth
                multiline
                rows={2}
                placeholder="Alamat lengkap customer"
              />
                
              <Box display="flex" gap={2}>
                <TextField
                  label="Username"
                  value={formData.username}
                  onChange={handleInputChange('username')}
                  required
                  fullWidth
                  placeholder="john_doe"
                />
                <TextField
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  fullWidth
                  placeholder="Kosongkan jika tidak ingin mengubah"
                  helperText="Kosongkan jika tidak ingin mengubah password"
                />
              </Box>
                
              <Box display="flex" gap={2}>
                <TextField
                  label="Profile"
                  value={formData.profile}
                  onChange={handleInputChange('profile')}
                  fullWidth
                  placeholder="Profile paket"
                />
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={handleSelectChange('status')}
                    label="Status"
                  >
                    <MenuItem value="active">Aktif</MenuItem>
                    <MenuItem value="inactive">Non-aktif</MenuItem>
                    <MenuItem value="suspended">Ditangguhkan</MenuItem>
                    <MenuItem value="free">Free</MenuItem>
                    <MenuItem value="NonInternet">Non Internet</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box display="flex" gap={2}>
                <TextField
                  label="Tanggal Aktivasi"
                  type="date"
                  value={formData.activation_date}
                  onChange={handleInputChange('activation_date')}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  helperText="Pilih tanggal aktivasi customer"
                />
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={generateCustomerCode}
                  disabled={!canGenerateCode() || isGeneratingCode}
                  sx={{ minWidth: 200 }}
                >
                  {isGeneratingCode ? 'Generating...' : 'Generate Code'}
                </Button>
              </Box>

              {/* Code Generation Status */}
              <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, fontSize: '0.875rem' }}>
                <Typography variant="body2" color="text.secondary">
                  {getCodeGenerationMessage()}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }}>
                <Typography variant="h6" component="div" sx={{ px: 2 }}>
                  PPP Secret Management
                </Typography>
              </Divider>

              {/* PPP Management Section */}
              <Box sx={{ p: 2, backgroundColor: 'info.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Kelola PPP Secret:
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={createPPPSecret}
                    disabled={!canCreatePPP() || isCreatingPPP}
                    size="small"
                  >
                    {isCreatingPPP ? 'Mencari...' : '🔄 Sinkronisasi PPP'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setShowCreatePPPDialog(true)}
                    disabled={!canCreatePPP() || isCreatingPPP}
                    size="small"
                  >
                    ➕ Buat PPP Baru
                  </Button>
                </Box>
                
                {!canCreatePPP() && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.75rem' }}>
                    Lengkapi Router, Kode Customer, dan Nama untuk menggunakan fitur PPP
                  </Typography>
                )}
              </Box>

              {/* PPP Status Display */}
              {pppMessage && (
                <Alert 
                  severity={pppCreationStatus === 'success' ? 'success' : pppCreationStatus === 'error' ? 'error' : 'info'}
                  sx={{ mt: 1 }}
                >
                  {pppMessage}
                </Alert>
              )}

              <FormControl fullWidth required>
                <InputLabel>SPOP</InputLabel>
                <Select
                  value={formData.spop_id}
                  onChange={(e) => {
                    const spopId = e.target.value as string;
                    handleSelectChange('spop_id')(e);
                  }}
                  label="SPOP"
                  disabled={spopsLoading}
                >
                  <MenuItem value="">
                    <em>Pilih SPOP</em>
                  </MenuItem>
                  {spopsData?.map((spop: any) => (
                    <MenuItem key={spop.id} value={spop.id}>
                      {spop.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Package</InputLabel>
                <Select
                  value={formData.package_id || ''}
                  onChange={handleSelectChange('package_id')}
                  label="Package"
                  disabled={packagesLoading || !formData.spop_id}
                >
                  <MenuItem value="">
                    <em>Pilih Package</em>
                  </MenuItem>
                  {packagesData?.map((pkg: any) => (
                    <MenuItem key={pkg.id} value={pkg.id}>
                      {pkg.name} - {
                        pkg.bandwidth_upload && pkg.bandwidth_download 
                          ? `↑${pkg.bandwidth_upload} Mbps / ↓${pkg.bandwidth_download} Mbps` 
                          : pkg.bandwidth_download 
                          ? `${pkg.bandwidth_download} Mbps` 
                          : 'No bandwidth info'
                      }
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
                
              <FormControl fullWidth required>
                <InputLabel>Router</InputLabel>
                <Select
                  value={formData.router_id}
                  onChange={handleSelectChange('router_id')}
                  label="Router"
                  disabled={routersLoading}
                >
                  <MenuItem value="">
                    <em>Pilih Router</em>
                  </MenuItem>
                  {routersData?.map((router: any) => (
                    <MenuItem key={router.id} value={router.id}>
                      {router.name} - {router.ip_address}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
                
              <Box display="flex" gap={2}>
                <FormControl fullWidth>
                  <InputLabel>ODP</InputLabel>
                  <Select
                    value={formData.odp_id || ''}
                    onChange={handleSelectChange('odp_id')}
                    label="ODP"
                    disabled={odpsLoading || !formData.spop_id}
                  >
                    <MenuItem value="">
                      <em>Pilih ODP</em>
                    </MenuItem>
                    {odpsData?.map((odp: any) => (
                      <MenuItem key={odp.id} value={odp.id}>
                        {odp.fullCode} - {odp.odpCode}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {updateCustomerMutation.error && (
                <Alert severity="error">
                  {(() => {
                    const err = updateCustomerMutation.error as {
                      response?: { data?: { message?: string; errors?: Array<{ field?: string; message?: string }> } };
                    };
                    const apiErrors = err.response?.data?.errors;
                    if (apiErrors?.length) {
                      return apiErrors.map((e) => e.message || e.field).join('. ');
                    }
                    return err.response?.data?.message || 'Error saat mengupdate customer';
                  })()}
                </Alert>
              )}
            </Box>
          </DialogContent>
          
          <DialogActions>
            <Button 
              onClick={handleClose}
              disabled={updateCustomerMutation.isPending}
            >
              Batal
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                updateCustomerMutation.isPending ||
                !formData.name ||
                !formData.email ||
                !formData.username ||
                !formData.router_id
              }
              startIcon={updateCustomerMutation.isPending ? <CircularProgress size={16} /> : null}
            >
              {updateCustomerMutation.isPending ? 'Menyimpan...' : 'Update Customer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* PPP Secret Selection Dialog */}
      <PPPSecretSelectionDialog
        open={showPPPSelectionDialog}
        onClose={() => setShowPPPSelectionDialog(false)}
        onSelect={handlePPPSecretSelection}
        onCreateNew={handleCreateNewPPPSecret}
        secrets={existingPPPSecrets}
        isLoading={isLoadingPPPSecrets}
        error={pppSecretsError}
      />

      {/* Create PPP Secret Dialog */}
      <CreatePPPSecretDialog
        open={showCreatePPPDialog}
        onClose={() => setShowCreatePPPDialog(false)}
        onCreateSecret={handleCreatePPPSecretSubmit}
        routerId={formData.router_id}
        routerName={routersData?.find((r: any) => String(r.id) === formData.router_id)?.name}
        isLoading={isCreatingPPP}
      />

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onClose={handleSuccessDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          ✅ Update Berhasil
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
              {successMessage}
            </Typography>
            
            {pppUpdateResult && (
              <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Status Update MikroTik:
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  {pppUpdateResult.success ? (
                    <Chip 
                      label="Berhasil" 
                      color="success" 
                      size="small" 
                      icon={<span>✅</span>}
                    />
                  ) : (
                    <Chip 
                      label="Peringatan" 
                      color="warning" 
                      size="small" 
                      icon={<span>⚠️</span>}
                    />
                  )}
                  <Typography variant="body2">
                    {pppUpdateResult.message}
                  </Typography>
                </Box>
              </Box>
            )}
            
            {!pppUpdateResult && (
              <Typography variant="body2" color="text.secondary">
                Customer berhasil diupdate di database. Tidak ada perubahan pada PPP Secret di MikroTik.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessDialogClose} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditCustomerDialog;
